// Date-range presets and labels (pure; no React) — used by DateRangePicker, FilterBar and pages.
// Days are store-local "YYYY-MM-DD" keys computed in the store timezone.
import { addDays, dayLabel, getFormatDefaults, today as todayKey } from '../../lib/format'

const parse = (k: string) => { const [y, m, d] = k.split('-').map(Number); return { y, m: m - 1, d } }
const key = (y: number, m: number, d: number) => { const t = new Date(Date.UTC(y, m, d)); return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}` }

export type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'last_90_days' | 'this_month' | 'last_month' | 'this_year' | 'last_12_months' | 'custom'
export interface DateRange { from: string; to: string; preset?: DatePreset }

export const DATE_PRESETS: Array<{ id: Exclude<DatePreset, 'custom'>; label: string }> = [
  { id: 'today', label: 'Today' }, { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 days' }, { id: 'last_30_days', label: 'Last 30 days' }, { id: 'last_90_days', label: 'Last 90 days' },
  { id: 'this_month', label: 'Month to date' }, { id: 'last_month', label: 'Last month' },
  { id: 'this_year', label: 'Year to date' }, { id: 'last_12_months', label: 'Last 12 months' },
]

/** Resolve a preset to concrete days in the store timezone (inclusive range). */
export function resolvePreset(preset: Exclude<DatePreset, 'custom'>, tz = getFormatDefaults().timeZone): DateRange {
  const t = todayKey(tz)
  const { y, m } = parse(t)
  switch (preset) {
    case 'today': return { from: t, to: t, preset }
    case 'yesterday': { const d = addDays(t, -1); return { from: d, to: d, preset } }
    case 'last_7_days': return { from: addDays(t, -6), to: t, preset }
    case 'last_30_days': return { from: addDays(t, -29), to: t, preset }
    case 'last_90_days': return { from: addDays(t, -89), to: t, preset }
    case 'this_month': return { from: key(y, m, 1), to: t, preset }
    case 'last_month': return { from: key(y, m - 1, 1), to: key(y, m, 0), preset }
    case 'this_year': return { from: key(y, 0, 1), to: t, preset }
    case 'last_12_months': return { from: addDays(key(y - 1, m, parse(t).d), 1), to: t, preset }
  }
}

/** The equally long period right before a range (for "vs previous period" comparisons). */
export function previousPeriod(r: DateRange): DateRange {
  const len = Math.round((Date.parse(`${r.to}T12:00:00Z`) - Date.parse(`${r.from}T12:00:00Z`)) / 86_400_000) + 1
  return { from: addDays(r.from, -len), to: addDays(r.from, -1), preset: 'custom' }
}

/** Human label: preset name, or "1 – 26 Sep 2026". */
export function rangeLabel(r: DateRange | null | undefined, locale = getFormatDefaults().locale): string {
  if (!r) return 'Any time'
  const p = r.preset && r.preset !== 'custom' ? DATE_PRESETS.find(x => x.id === r.preset) : null
  if (p) return p.label
  if (r.from === r.to) return dayLabel(r.from, 'medium', locale)
  const a = parse(r.from), b = parse(r.to)
  try {
    const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) as Intl.DateTimeFormat & { formatRange?: (x: Date, y: Date) => string }
    if (fmt.formatRange) return fmt.formatRange(new Date(Date.UTC(a.y, a.m, a.d, 12)), new Date(Date.UTC(b.y, b.m, b.d, 12)))
  } catch { /* fall through */ }
  return `${dayLabel(r.from, 'medium', locale)} – ${dayLabel(r.to, 'medium', locale)}`
}

